/**
 * DATA LAYER (DL): PERSONAL ROULETTE (RULETA BAJA CUP)
 * Comunicación HTTP Fetch con la API REST de Interaktia-V2 para Ruleta Baja Cup.
 */

const getEndpointUrl = (subPath) => {
    let base = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    if (!base.endsWith('/')) {
        base += '/';
    }
    const cleanSub = subPath.startsWith('/') ? subPath.slice(1) : subPath;
    if (base.endsWith('/api/')) {
        if (cleanSub.startsWith('api/')) {
            return `${base}${cleanSub.slice(4)}`;
        }
        return `${base}${cleanSub}`;
    } else {
        if (cleanSub.startsWith('api/')) {
            return `${base}${cleanSub}`;
        }
        return `${base}api/${cleanSub}`;
    }
};

const getAuthHeaders = (isJson = true) => {
    const token = localStorage.getItem('interaktia_token');
    const headers = {};
    if (isJson) {
        headers['Content-Type'] = 'application/json';
    }
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

/**
 * Obtiene la configuración pública de la Ruleta para clientes móviles y stands.
 * GET /api/v2/personal-roulette/config?roomId={roomId}
 * @param {string|null} roomId 
 */
export const GetPublicConfig = async (roomId = null) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        const url = `${getEndpointUrl('api/v2/personal-roulette/config')}${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || 'Error al obtener la configuración pública de la ruleta.'
            };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar la configuración de la ruleta.' };
    }
};

/**
 * Inicializa y valida el registro de un aficionado generando su One-Time Token (OTT).
 * POST /api/v2/personal-roulette/participate-init
 * @param {object} payload 
 */
export const ParticipateInit = async (payload) => {
    try {
        const cleanPayload = { ...payload };
        if (!cleanPayload.roomId) {
            delete cleanPayload.roomId;
        }
        const url = getEndpointUrl('api/v2/personal-roulette/participate-init');
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cleanPayload)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: data.message || 'Error al registrar al participante en la ruleta.',
                data
            };
        }

        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al inicializar la participación.' };
    }
};

/**
 * Registra un evento de telemetría y embudo de conversión para la dinámica.
 * POST /api/v2/personal-roulette/track
 * @param {object} payload 
 * @param {string|null} roomId 
 */
export const TrackMetric = async (payload, roomId = null) => {
    try {
        const effectiveRoomId = (payload && payload.roomId) || roomId || null;
        const dataToSend = {
            eventName: payload?.eventName,
            sessionId: payload?.sessionId,
            source: payload?.source || 'direct',
            ...(effectiveRoomId ? { roomId: effectiveRoomId } : {})
        };
        const url = getEndpointUrl('api/v2/personal-roulette/track');

        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataToSend)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || 'Error al registrar métrica de telemetría.'
            };
        }

        const data = await response.json().catch(() => ({ success: true }));
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al registrar métrica.' };
    }
};

/**
 * Ejecuta el giro seguro en el servidor consumiendo el OTT (HU-02).
 * POST /api/v2/personal-roulette/spin
 * @param {object} payload 
 */
export const SpinRoulette = async (payload) => {
    try {
        const cleanPayload = { ...payload };
        if (!cleanPayload.roomId) {
            delete cleanPayload.roomId;
        }
        const url = getEndpointUrl('api/v2/personal-roulette/spin');
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cleanPayload)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            let errorMsg = data.message || data.title;
            if (!errorMsg && data.errors) {
                errorMsg = Object.values(data.errors).flat().join(', ');
            }
            return {
                success: false,
                message: errorMsg || 'Error al ejecutar el giro de la ruleta.',
                data
            };
        }

        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al ejecutar el giro de la ruleta.' };
    }
};

/**
 * Registra una acción sobre el cupón digital (ej. DOWNLOAD, RECOVER) (HU-03).
 * POST /api/v2/personal-roulette/track-action
 * @param {object} payload 
 */
export const TrackAction = async (payload) => {
    try {
        const cleanPayload = { ...payload };
        if (!cleanPayload.roomId) {
            delete cleanPayload.roomId;
        }
        const url = getEndpointUrl('api/v2/personal-roulette/track-action');
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cleanPayload)
        });

        const data = await response.json().catch(() => ({}));
        return { success: response.ok, data };
    } catch (error) {
        return { success: false, message: 'Error de red al registrar acción del cupón.' };
    }
};

/**
 * Obtiene la configuración administrativa completa (HU-04).
 * GET /api/v2/admin/personal-roulette/config?roomId={roomId}
 * @param {string|null} roomId 
 */
export const GetAdminConfig = async (roomId = null) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        const url = `${getEndpointUrl('api/v2/admin/personal-roulette/config')}${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al obtener la configuración de administración.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar la configuración administrativa.' };
    }
};

/**
 * Actualiza la configuración administrativa de la ruleta (HU-04).
 * POST /api/v2/admin/personal-roulette/config
 * @param {object} payload 
 */
export const UpdateAdminConfig = async (payload) => {
    try {
        const cleanPayload = { ...payload };
        if (cleanPayload.prizeQueue && Array.isArray(cleanPayload.prizeQueue)) {
            cleanPayload.prizeQueue = cleanPayload.prizeQueue.map(item => {
                if (typeof item === 'string') return item;
                return item?.prizeId || item?.id || '';
            }).filter(Boolean);
        }

        const url = getEndpointUrl('api/v2/admin/personal-roulette/config');
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(cleanPayload)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            let errorMsg = data.message || 'Error al guardar la configuración de la ruleta.';
            if (data.errors && typeof data.errors === 'object') {
                const detailedErrors = Object.values(data.errors).flat().filter(Boolean);
                if (detailedErrors.length > 0) {
                    errorMsg = detailedErrors.join(' ');
                }
            }
            return {
                success: false,
                message: errorMsg,
                data
            };
        }

        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al actualizar la configuración de la ruleta.' };
    }
};

/**
 * Obtiene las métricas y estadísticas del embudo de conversión administrativo (HU-04).
 * GET /api/v2/admin/personal-roulette/stats?roomId={roomId}
 * @param {string|null} roomId 
 */
export const GetAdminStats = async (roomId = null) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        const url = `${getEndpointUrl('api/v2/admin/personal-roulette/stats')}${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || 'Error al obtener estadísticas de la ruleta.'
            };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar estadísticas de la ruleta.' };
    }
};

/**
 * Obtiene el listado paginado y filtrado de participantes para la mesa de control (HU-04).
 * GET /api/v2/admin/personal-roulette/participants?...
 * @param {string|null} roomId 
 * @param {number} page 
 * @param {number} pageSize 
 * @param {string} search 
 * @param {string} status 
 */
export const GetAdminParticipants = async (roomId = null, page = 1, pageSize = 25, search = '', status = '') => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        if (page) query.append('page', page);
        if (pageSize) query.append('pageSize', pageSize);
        if (search) query.append('search', search);
        if (status && status !== 'all') query.append('status', status);

        const url = `${getEndpointUrl('api/v2/admin/personal-roulette/participants')}?${query.toString()}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || 'Error al consultar lista de participantes.'
            };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar participantes de la ruleta.' };
    }
};

/**
 * Alterna el estado de activación de registros de la ruleta (HU-04).
 * POST /api/v2/admin/personal-roulette/toggle-active
 * @param {boolean|object} payloadOrIsActive 
 * @param {string|null} roomId 
 */
export const ToggleActivePersonalRoulette = async (payloadOrIsActive, roomId = null) => {
    try {
        let payload = {};
        if (typeof payloadOrIsActive === 'object' && payloadOrIsActive !== null) {
            payload = payloadOrIsActive;
        } else {
            payload = {
                isActive: Boolean(payloadOrIsActive),
                ...(roomId ? { roomId } : {})
            };
        }
        const url = getEndpointUrl('api/v2/admin/personal-roulette/toggle-active');

        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al actualizar el estado de la ruleta.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al actualizar el estado de la ruleta.' };
    }
};

/**
 * Reinicia por completo los datos de la sala en la ruleta (Zona Peligrosa) (HU-04).
 * POST /api/v2/admin/personal-roulette/reset-data
 * @param {object|string} payload 
 */
export const ResetDataPersonalRoulette = async (payload = {}) => {
    try {
        const bodyPayload = typeof payload === 'string'
            ? { roomId: payload, password: 'evitarborrarregistros456852' }
            : {
                password: 'evitarborrarregistros456852',
                ...payload
            };
        if (!bodyPayload.roomId) delete bodyPayload.roomId;

        const url = getEndpointUrl('api/v2/admin/personal-roulette/reset-data');

        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(bodyPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al reiniciar datos de la ruleta.' };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al reiniciar datos de la ruleta.' };
    }
};

/**
 * Obtiene el dataset completo para exportación a Excel con 3 hojas estructuradas (HU-04).
 * GET /api/v2/admin/personal-roulette/export-dataset?roomId={roomId}
 * @param {string|null} roomId 
 */
export const GetExportDataset = async (roomId = null) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        const url = `${getEndpointUrl('api/v2/admin/personal-roulette/export-dataset')}${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || 'Error al obtener dataset de exportación.'
            };
        }

        const data = await response.json();
        return { success: true, data: data.data || data };
    } catch (error) {
        return { success: false, message: 'Error de red al obtener dataset de exportación.' };
    }
};

/**
 * Consulta y pre-valida un folio, teléfono o código QR para canje en sitio (HU-05).
 * POST /api/v2/admin/personal-roulette/lookup
 * @param {object} payload { roomId, passCode, phone, search }
 */
export const LookupVoucher = async (payload = {}) => {
    try {
        const url = getEndpointUrl('api/v2/admin/personal-roulette/lookup');
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                status: data.status || 'ERROR',
                message: data.message || 'Error al validar el folio o teléfono para canje.',
                data: data.data || data
            };
        }

        return {
            success: true,
            status: data.status || 'SUCCESS',
            message: data.message || 'Participante encontrado con éxito.',
            data: data.data || data
        };
    } catch (error) {
        return {
            success: false,
            status: 'NETWORK_ERROR',
            message: 'Error de red al consultar el folio para canje.'
        };
    }
};

/**
 * Registra y ejecuta la entrega y canje físico del premio en sitio (HU-05).
 * POST /api/v2/admin/personal-roulette/redeem
 * @param {object} payload { roomId, passCode, redeemChannel, operatorName, notes }
 */
export const RedeemPrize = async (payload = {}) => {
    try {
        const url = getEndpointUrl('api/v2/admin/personal-roulette/redeem');
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                status: data.status || 'ERROR',
                message: data.message || 'Error al registrar el canje del premio.',
                data: data.data || data
            };
        }

        return {
            success: true,
            status: data.status || 'REDEEMED_SUCCESS',
            message: data.message || '¡Premio canjeado con éxito!',
            data: data.data || data
        };
    } catch (error) {
        return {
            success: false,
            status: 'NETWORK_ERROR',
            message: 'Error de red al ejecutar el canje del premio.'
        };
    }
};

/**
 * Restituye la oportunidad de un participante archivando su intento actual (HU-06).
 * POST /api/v2/admin/personal-roulette/restitute
 * @param {object} payload { participantId, reason, operatorUser, roomId }
 */
export const RestituteParticipant = async (payload = {}) => {
    try {
        const url = getEndpointUrl('api/v2/admin/personal-roulette/restitute');
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: data.message || 'Error al restituir la oportunidad del participante.',
                data
            };
        }

        return {
            success: true,
            message: data.message || 'Participante restituido exitosamente.',
            data: data.data || data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al restituir la participación.'
        };
    }
};

/**
 * Actualiza el índice / puntero del paso actual en la fila de premios secuencial (HU-07).
 * POST /api/v2/admin/personal-roulette/queue/set-index
 * @param {object} payload { roomId, newIndex, index }
 */
export const SetQueueIndex = async (payload = {}) => {
    try {
        const url = getEndpointUrl('api/v2/admin/personal-roulette/queue/set-index');
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: data.message || 'Error al cambiar el índice de la fila de premios.',
                data
            };
        }

        return {
            success: true,
            message: data.message || 'Puntero de la fila actualizado con éxito.',
            data: data.data || data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al actualizar el índice de la fila.'
        };
    }
};

/**
 * Actualiza la lista ordenada de la fila de premios secuencial en caliente (HU-07).
 * POST /api/v2/admin/personal-roulette/queue/update
 * @param {object} payload { roomId, prizeQueue, prizeQueueList }
 */
export const UpdatePrizeQueue = async (payload = {}) => {
    try {
        const cleanPayload = { ...payload };
        const rawQueue = cleanPayload.prizeQueue || cleanPayload.prizeQueueList || [];
        if (Array.isArray(rawQueue)) {
            cleanPayload.prizeQueue = rawQueue.map(item => {
                if (typeof item === 'string') return item;
                return item?.prizeId || item?.id || '';
            }).filter(Boolean);
        }

        const url = getEndpointUrl('api/v2/admin/personal-roulette/queue/update');
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(cleanPayload)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            let errorMsg = data.message || 'Error al actualizar la fila de premios.';
            if (data.errors && typeof data.errors === 'object') {
                const detailedErrors = Object.values(data.errors).flat().filter(Boolean);
                if (detailedErrors.length > 0) {
                    errorMsg = detailedErrors.join(' ');
                }
            }
            return {
                success: false,
                message: errorMsg,
                data
            };
        }

        return {
            success: true,
            message: data.message || 'Fila de premios actualizada con éxito.',
            data: data.data || data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al actualizar la fila de premios.'
        };
    }
};

/**
 * Activa o desactiva el Modo VIP para garantizar victoria en el próximo tiro (HU-07).
 * POST /api/v2/admin/personal-roulette/queue/vip-boost
 * @param {object} payload { roomId, isGuaranteed, isGuaranteedWinner, overridePrizeId }
 */
export const ToggleVipBoost = async (payload = {}) => {
    try {
        const url = getEndpointUrl('api/v2/admin/personal-roulette/queue/vip-boost');
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            return {
                success: false,
                message: data.message || 'Error al modificar el estado del Modo VIP.',
                data
            };
        }

        return {
            success: true,
            message: data.message || 'Modo VIP actualizado exitosamente.',
            data: data.data || data
        };
    } catch (error) {
        return {
            success: false,
            message: 'Error de red al modificar el Modo VIP.'
        };
    }
};



